import { useCallback, useEffect, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
// @mui
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
//
import axios, { endpoints } from 'src/utils/axios';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import Iconify from 'src/components/iconify';
import Label from 'src/components/label';
import { IftaFiling, PAYMENT_STATUS_LABELS } from 'src/models';
import { activeStep, WIZARD_STEPS } from 'src/sections/filing-wizard/wizard-steps';

// ----------------------------------------------------------------------

const STATUS_COLORS = { unpaid: 'warning', pending: 'info', paid: 'success' } as const;

export default function FilingsListPage() {
  const router = useRouter();
  const [filings, setFilings] = useState<IftaFiling[] | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const response = await axios.get<IftaFiling[]>(endpoints.filings.findAll);
    setFilings(response.data || []);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startFiling = async () => {
    setCreating(true);
    try {
      const response = await axios.post<IftaFiling>(endpoints.filings.create, {
        title: 'IFTA Filing',
      });
      router.push(paths.filings.wizard(response.data.id));
    } catch (error) {
      const message = error?.response?.data?.[0]?.message;
      enqueueSnackbar(message || 'Could not start a filing', { variant: message ? 'warning' : 'error' });
    } finally {
      setCreating(false);
    }
  };

  if (!filings) {
    return (
      <Stack alignItems="center" sx={{ py: 10 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h4">My IFTA filings</Typography>
        {filings.length > 0 && (
          <Button
            variant="contained"
            color="success"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={startFiling}
            disabled={creating}
            data-testid="new-filing-button"
          >
            Start new filing
          </Button>
        )}
      </Stack>

      {filings.length === 0 && (
        <Card sx={{ p: 6, textAlign: 'center' }} data-testid="filings-empty">
          <Stack spacing={2} alignItems="center">
            <Iconify icon="solar:document-add-bold" width={48} sx={{ color: 'text.disabled' }} />
            <Typography variant="h6">No filings yet</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 420 }}>
              Start your first IFTA filing: pick the quarter, import your miles and fuel, and get an
              audit-ready calculation in minutes.
            </Typography>
            <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={startFiling}
              disabled={creating}
              data-testid="new-filing-button"
              sx={{ mt: 2 }}
            >
              Start new filing
            </Button>
          </Stack>
        </Card>
      )}

      {filings.map((filing) => {
        const paid = filing.paymentStatus === 'paid';
        const step = activeStep(filing);
        return (
          <Card key={filing.id} variant="outlined">
            <CardActionArea
              onClick={() =>
                router.push(paid ? paths.filings.report(filing.id) : paths.filings.wizard(filing.id))
              }
              sx={{ p: 2.5 }}
              data-testid={`filing-card-${filing.id}`}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Stack sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1">
                    {filing.title}
                    {filing.selectedQuarter ? ` · ${filing.selectedQuarter}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {paid
                      ? `${filing.truckCount} trucks · report ready`
                      : `Next step: ${WIZARD_STEPS[step]}`}
                  </Typography>
                </Stack>
                <Label
                  color={
                    paid
                      ? (filing.filedAt && 'success') || 'info'
                      : STATUS_COLORS[filing.paymentStatus] || 'default'
                  }
                >
                  {paid
                    ? (filing.filedAt && 'Filed') || 'Calculated'
                    : PAYMENT_STATUS_LABELS[filing.paymentStatus] || filing.paymentStatus}
                </Label>
                <Iconify icon="eva:arrow-ios-forward-fill" />
              </Stack>
            </CardActionArea>
          </Card>
        );
      })}
    </Stack>
  );
}
